import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { CardCvvElement, CardNumberElement, CardMonthElement, CardYearElement } from "@recurly/react-recurly";
import { BillingInfo } from "lens-platform-sdk";
import { FormField } from "@k8slens/lds-form";
import { commerce, user, misc } from "@k8slens/lds-icons";
import { type Props as IconProps } from "@k8slens/lds-icons/lib/es/Icon/Icon";

import { formatCreditCardNumber, defaultObstructedCharacter } from "src/utils/credit-card";

import type { CCFormErrors, RecurlyStyleProps } from "../types";
import { recurlyStyle } from "../utils";

import styles from "./CreditCardForm.module.css";

type CreditCardFormProps = {
  className?: string;
  loading?: boolean;
  paymentMethod?: BillingInfo["paymentMethod"];
  firstName?: string;
  lastName?: string;
  errors?: CCFormErrors;
};

const { EventIcon } = misc;
const { CreditCardIcon } = commerce;
const { DialpadIcon, StylusNoteIcon } = user;

const inputWrapperClassname = "lds-input--wrapper lds-input--with-icon";
const defaultInputClassname = clsx("lds-input", styles.input);
const iconProps: IconProps = { className: clsx("lds-input--icon", styles.icon), size: "sm" };

type InputType = "credit-card" | "cvv" | "expiration" | "name";

const getInputListeners = (type: InputType, setFocus: (focus: "" | InputType) => void) => ({
  onFocus: () => setTimeout(() => setFocus(type), 0),
  onBlur: () => setFocus(""),
});

const getInputClassName = (type: InputType, loading?: boolean, focus?: "" | InputType) => ({
  [styles.disabled]: loading,
  [styles.focused]: focus === type,
});

const getFormFieldProps = (label: string, name: string, className?: string) => ({
  label,
  name,
  id: name,
  errorId: `${name}-error`,
  labelId: `${name}-label`,
  required: true,
  className,
});

export const CreditCardForm = ({
  loading,
  paymentMethod,
  firstName,
  lastName,
  className,
  errors,
}: CreditCardFormProps) => {
  const [focus, setFocus] = useState<"" | InputType>("");

  const [hasPrefilledValues, setHasPrefilledValues] = useState(false);
  const [numberPlaceholder, setNumberPlaceholder] = useState("1234 5678 9012 3456");
  const [expirationMonthPlaceholder, setExpirationMonthPlaceholder] = useState("MM");
  const [expirationYearPlaceholder, setExpirationYearPlaceholder] = useState("YY");
  const [verificationCodePlaceholder, setVerificationCodePlaceholder] = useState("123");

  const getRecurlyInputProps = useCallback(
    ({ style, type, className }: { style?: RecurlyStyleProps; type: InputType; className?: string }) => {
      return {
        className: clsx(className, getInputClassName(type, loading, focus)),
        style: recurlyStyle(style, focus === type, hasPrefilledValues),
        ...getInputListeners(type, setFocus),
      };
    },
    [loading, focus, setFocus, hasPrefilledValues],
  );

  useEffect(() => {
    if (paymentMethod) {
      const { firstSix, lastFour, expMonth, expYear } = paymentMethod;

      if (firstSix && lastFour && expMonth && expYear) {
        // Set the initial values for the card number and expiration date
        setNumberPlaceholder(formatCreditCardNumber(paymentMethod));
        setExpirationMonthPlaceholder(expMonth.toString().padStart(2, "0"));
        setExpirationYearPlaceholder(expYear.toString().slice(-2));
        setVerificationCodePlaceholder(new Array(3).fill(defaultObstructedCharacter).join(""));
        setHasPrefilledValues(true);
      }
    }
  }, [paymentMethod]);

  const numberErrors = errors?.number || [];
  const expirationErrors = [...(errors?.month || []), ...(errors?.year || [])];
  const cvvErrors = errors?.cvv || [];
  const nameErrors = [...(errors?.first_name || []), ...(errors?.last_name || [])];

  return (
    <div className={className}>
      <fieldset className={clsx(styles.fieldset)}>
        <FormField
          {...getFormFieldProps("Credit Card Number", "credit-card", styles.gridItemWide)}
          errors={numberErrors}
        >
          <div className={inputWrapperClassname}>
            <CreditCardIcon {...iconProps} />
            <CardNumberElement
              {...getRecurlyInputProps({
                type: "credit-card",
                className: clsx(defaultInputClassname, styles.number, { [styles.error]: numberErrors.length }),
                style: { placeholder: numberPlaceholder },
              })}
            />
          </div>
        </FormField>
        <FormField {...getFormFieldProps("Expiration Date", "credit-card-expiration")} errors={expirationErrors}>
          <div className={inputWrapperClassname}>
            <EventIcon {...iconProps} />
            <div
              className={clsx(
                defaultInputClassname,
                styles.expiration,
                getInputClassName("expiration", loading, focus),
              )}
              {...(expirationErrors.length ? { "aria-invalid": true } : {})}
            >
              <CardMonthElement
                {...getRecurlyInputProps({
                  type: "expiration",
                  style: { textAlign: "right", placeholder: expirationMonthPlaceholder },
                  className: styles.month,
                })}
              />
              <span className={styles.expirationCaret}>/</span>
              <CardYearElement
                {...getRecurlyInputProps({
                  type: "expiration",
                  style: { placeholder: expirationYearPlaceholder },
                  className: styles.year,
                })}
              />
            </div>
          </div>
        </FormField>
        <FormField {...getFormFieldProps("CVV Number", "credit-card-cvv")} errors={cvvErrors}>
          <div className={inputWrapperClassname}>
            <DialpadIcon {...iconProps} />
            <CardCvvElement
              {...getRecurlyInputProps({
                type: "cvv",
                className: clsx(defaultInputClassname, styles.cvv, { [styles.error]: cvvErrors.length }),
                style: { placeholder: verificationCodePlaceholder },
              })}
            />
          </div>
        </FormField>
        <FormField
          {...getFormFieldProps("Name on Credit Card", "credit-card-name", styles.gridItemWide)}
          errors={nameErrors}
        >
          <div className={inputWrapperClassname}>
            <StylusNoteIcon {...iconProps} />
            <div
              className={clsx(defaultInputClassname, styles.name, {
                [styles.disabled]: loading,
                [styles.focused]: focus === "name",
              })}
              {...(nameErrors.length ? { "aria-invalid": true } : {})}
            >
              <input
                className={styles.hiddenInput}
                type="text"
                name="credit-card-first-name"
                data-recurly="first_name"
                placeholder="First"
                defaultValue={firstName}
                {...getInputListeners("name", setFocus)}
              />
              <input
                className={styles.hiddenInput}
                type="text"
                name="credit-card-last-name"
                data-recurly="last_name"
                defaultValue={lastName}
                placeholder="Last"
                {...getInputListeners("name", setFocus)}
              />
            </div>
          </div>
        </FormField>
      </fieldset>
    </div>
  );
};
